import App from "../app";
import {
	ChangeAdvanceIntervalEvent, ChangeFontSizeEvent, KeyboardShortcutsEvent,
	ReportErrorEvent, ShowReportModalEvent, TickEvent, ToggleAdvanceEvent,
	ToggleShortcutModalEvent, ToggleTimerEvent, EnterEmailEvent, InputOverrideEvent
} from "../events";
import { ListItem } from "../model/datasource/list";
import { ContentItem } from "../model/datasource/item";
import { ErrorReportResponse } from "../model/datasource/settings";
import Controller from "./controller";
import { AttemptId, ProductId, ItemId, QuizId } from "../model/datasource";

export class SettingsController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends Controller<P, A, Z, I> {
	bind(target: EventTarget, app: App<P, A, Z, I>): void {
		target.addEventListener(ChangeFontSizeEvent.type, (e: ChangeFontSizeEvent) => this.changeFontSize(app, e.detail));
		target.addEventListener(KeyboardShortcutsEvent.type, () => this.keyboardShortcuts(app));
		target.addEventListener(ToggleTimerEvent.type, () => this.toggleTimer(app));
		target.addEventListener(ToggleShortcutModalEvent.type, () => this.toggleShortcutModal(app));
		target.addEventListener(ShowReportModalEvent.type, () => this.showReportModal(app));
		target.addEventListener(EnterEmailEvent.type, (e: EnterEmailEvent) => this.enterEmail(app, e.detail));
		target.addEventListener(InputOverrideEvent.type, (e: InputOverrideEvent) => this.inputOverride(app, e.detail.root, e.detail.selector, e.detail.value));
		target.addEventListener(ReportErrorEvent.type, (e: ReportErrorEvent) => this.sendErrorReport(app, e.detail));
		target.addEventListener(ToggleAdvanceEvent.type, (e: ToggleAdvanceEvent) => this.toggleAutoAdvance(app, e.detail));
		target.addEventListener(ChangeAdvanceIntervalEvent.type, (e: ChangeAdvanceIntervalEvent) => this.changeAdvanceInterval(app, e.detail));
	}

	changeFontSize(app: App<P, A, Z, I>, fontsize: number): void {
		app.model.settings.setFontSize(fontsize);
		app.model.state.global.updateState({ settings: { fontsize } });
		app.view.settings.displayFontSize(fontsize);
		app.view.item.displayFontSize(fontsize);
	}

	keyboardShortcuts(app: App<P, A, Z, I>): void {
		const enabled: boolean = !app.model.settings.getKeyboardShortcuts();
		app.model.settings.setKeyboardShortcuts(enabled);
		app.view.settings.displayKeyboardShortcuts(enabled);
	}

	toggleTimer(app: App<P, A, Z, I>): void {
		const show: boolean = app.model.settings.toggleTimer();
		app.view.header.displayTimer(show);
		app.view.settings.displayTimer(show);
	}

	toggleShortcutModal(app: App<P, A, Z, I>): void {
		app.view.settings.toggleShortcutModal(app.getJquery());
	}

	sendErrorReport(app: App<P, A, Z, I>, form: HTMLFormElement): void {
		app.model.metadata.getProductId()
			.then((product: P) => app.model.settings.sendErrorReport(product, form))
			.then((result: ErrorReportResponse) => {
				if (result.success) {
					app.model.settings.clearErrors();
					return app.view.settings.hideErrorModal(app.getJquery(), result.success, result.reason);
				} else {
					return app.view.settings.displayReportErrorMessage(result.reason);
				}
			});
	}

	showReportModal(app: App<P, A, Z, I>): void {
		app.view.settings.displayErrorLoading(true);
		Promise.all([
			app.model.metadata.getUrl(),
			app.content.get(app.current.get()).catch(() => undefined),
			app.content.meta(app.current.get()),
			app.model.metadata.getScreenshot(),
			app.model.settings.getErrors(),
		]).then(([url, item, meta, screenshot, errors]:
			[url: string, item: ContentItem<I>, meta: ListItem<I>, screenshot: string, errors: (Error | any)[]
		]) => {
			const localStorage = app.model.settings.getLocalStorage();
			app.view.settings.displayErrorModal(app.getJquery(), url, item ?? meta, app.filter.getValues(), screenshot, errors, localStorage);
			app.view.settings.displayErrorLoading(false);
		});
	}

	async enterEmail(app: App<P, A, Z, I>, input: HTMLInputElement): Promise<void> {
		const suggestion = await app.model.settings.getEmailSuggestion(input.value);
		app.view.settings.displayInputSuggestion(input, suggestion);
	}

	inputOverride(app: App<P, A, Z, I>, root: HTMLElement, selector: string, value: string): void {
		const inputs = app.view.settings.updateInputs(root, selector, value);
		for (const input of inputs) {
			app.view.settings.displayInputSuggestion(input, '');
		}
	}

	toggleAutoAdvance(app: App<P, A, Z, I>, enable: boolean): void {
		const bindTarget: EventTarget = app.view.getBindTarget();
		app.timer.toggleAutoAdvance(enable);
		app.model.state.global.updateState({ timer: { autoAdvance: enable } });
		app.view.header.displayCountdown(enable);
		app.view.settings.displayAdvance(enable);
		bindTarget.dispatchEvent(new TickEvent());
	}

	changeAdvanceInterval(app: App<P, A, Z, I>, interval: number): void {
		const bindTarget: EventTarget = app.view.getBindTarget();
		app.timer.setAdvanceInterval(interval);
		app.model.state.global.updateState({ timer: { advanceRate: interval } });
		app.view.settings.displayAdvanceInterval(interval);
		bindTarget.dispatchEvent(new TickEvent());
	}
}